import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  MenuItem,
  Avatar,
  Radio,
  RadioGroup,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Alert,
  Autocomplete,
  IconButton,
  Switch,
  Divider,
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import BarcodeScannerComponent from 'react-qr-barcode-scanner';
import {
  useGetProductQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useGetCategoriesQuery,
} from '../../../store/api/productsApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';

function ProductForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const { data: product, isLoading: loadingProduct } = useGetProductQuery(id, {
    skip: !isEdit,
  });

  const [categorySearch, setCategorySearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(categorySearch), 500);
    return () => clearTimeout(handler);
  }, [categorySearch]);

  const [formData, setFormData] = useState({
    sku: '',
    name: '',
    description: '',
    barcode: '',
    categoryId: '',
    brand: '',
    baseUnit: 'pcs',
    purchaseUnit: 'pcs',
    conversionFactor: '1.00',
    productType: 'raw',
    costPrice: '',
    openingStock: '',
    isLiveKitchen: false,
    isActive: true,
  });

  const { data: categoriesData } = useGetCategoriesQuery({ 
    search: debouncedSearch,
    categoryType: formData.productType 
  });
  const [createProduct] = useCreateProductMutation();
  const [updateProduct] = useUpdateProductMutation();
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [scanError, setScanError] = useState('');

  useEffect(() => {
    if (!scanOpen) setScanError('');
  }, [scanOpen]);

  useEffect(() => {
    if (product) {
      setFormData({
        sku: product.sku || '',
        name: product.name || '',
        description: product.description || '',
        barcode: product.barcode || '',
        categoryId: product.categoryId || '',
        brand: product.brand || '',
        baseUnit: product.baseUnit || 'pcs',
        purchaseUnit: product.purchaseUnit || 'pcs',
        conversionFactor: product.conversionFactor != null ? String(product.conversionFactor) : '1.00',
        productType: product.productType || 'raw',
        costPrice: product.costPrice != null && product.costPrice !== '' && Number(product.costPrice) !== 0 ? String(product.costPrice) : '',
        openingStock: product.openingStock != null && product.openingStock !== '' && Number(product.openingStock) !== 0 ? String(product.openingStock) : '',
        isLiveKitchen: product.isLiveKitchen || false,
        isActive: product.isActive !== undefined ? product.isActive : true,
      });
      // Set image preview if product has an image
      if (product.imageUrl) {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
        const baseUrl = apiUrl.replace('/api', '');
        setImagePreview(`${baseUrl}${product.imageUrl}`);
      } else {
        setImagePreview(null);
      }
    }
  }, [product]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.name.trim()) {
      alert('Product name is required');
      return;
    }

    const sku = isEdit ? product.sku : `PRD-${Date.now()}`;


    try {
      // Create FormData if image is being uploaded, otherwise use regular object
      let submitData;
      
      if (imageFile) {
        // Use FormData when uploading an image
        submitData = new FormData();
        submitData.append('sku', sku);
        submitData.append('name', formData.name.trim());
        submitData.append('categoryId', formData.categoryId ? formData.categoryId : '');
        submitData.append('brand', formData.brand || '');
        submitData.append('baseUnit', formData.baseUnit || 'pcs');
        submitData.append('purchaseUnit', formData.purchaseUnit || 'pcs');
        submitData.append('conversionFactor', parseFloat(formData.conversionFactor) || 1.00);
        submitData.append('productType', formData.productType);
        submitData.append('isLiveKitchen', formData.isLiveKitchen);
        submitData.append('costPrice', parseFloat(formData.costPrice) || 0);
        if (formData.openingStock !== '' && formData.openingStock != null) {
          submitData.append('openingStock', parseFloat(formData.openingStock) || 0);
        }
        submitData.append('isActive', formData.isActive);
        submitData.append('image', imageFile);
      } else {
        // Use regular JSON object when no image
        submitData = {
          sku: sku,
          name: formData.name.trim(),
          categoryId: formData.categoryId || null,
          brand: formData.brand || '',
          baseUnit: formData.baseUnit || 'pcs',
          purchaseUnit: formData.purchaseUnit || 'pcs',
          conversionFactor: parseFloat(formData.conversionFactor) || 1.00,
          productType: formData.productType,
          isLiveKitchen: formData.isLiveKitchen,
          costPrice: parseFloat(formData.costPrice) || 0,
          openingStock: formData.openingStock !== '' && formData.openingStock != null ? parseFloat(formData.openingStock) : undefined,
          isActive: formData.isActive,
        };
      }

      if (isEdit) {
        await updateProduct(
          imageFile ? { id, body: submitData } : { id, ...submitData }
        ).unwrap();
      } else {
        await createProduct(submitData).unwrap();
      }
      navigate('/products');
    } catch (error) {
      console.error('Error saving product:', error);
      alert(error?.data?.message || 'Error saving product');
    }
  };

  if (loadingProduct) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        {isEdit ? 'Edit Product' : 'New Product'}
      </Typography>

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">Product Classification</Typography>
                <RadioGroup
                  row
                  name="productType"
                  value={formData.productType}
                  onChange={handleChange}
                >
                  <FormControlLabel value="raw" control={<Radio />} label="Raw Material (Ingredient)" />
                  <FormControlLabel value="finished" control={<Radio />} label="Finished Good (Menu Item)" />
                </RadioGroup>
              </Box>
              {formData.productType === 'finished' && (
                <FormControlLabel
                  control={
                    <Switch
                      name="isLiveKitchen"
                      checked={formData.isLiveKitchen}
                      onChange={(e) => setFormData({ ...formData, isLiveKitchen: e.target.checked })}
                      color="secondary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Live Kitchen Item</Typography>
                      <Typography variant="caption" color="text.secondary">Auto-consume ingredients when this item is sold</Typography>
                    </Box>
                  }
                />
              )}
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body2" fontWeight="medium">
                  Product Image
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  {imagePreview && (
                    <Avatar
                      src={imagePreview}
                      alt="Product preview"
                      sx={{ width: 120, height: 120 }}
                      variant="rounded"
                    />
                  )}
                  <Button variant="outlined" component="label">
                    {imagePreview ? 'Change Image' : 'Upload Image'}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                  </Button>
                  {imagePreview && (
                    <Button
                      variant="text"
                      color="error"
                      onClick={() => {
                        setImageFile(null);
                        setImagePreview(null);
                      }}
                    >
                      Remove
                    </Button>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Supported formats: JPEG, PNG, GIF, WebP (Max 5MB)
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                options={
                  categoriesData
                    ? [
                        ...categoriesData.filter((c) => c.parentId !== null),
                        ...categoriesData.filter((c) => c.parentId === null),
                      ]
                    : []
                }
                getOptionLabel={(option) =>
                  option.parent?.name
                    ? `${option.parent.name} > ${option.name}`
                    : `${option.name} (Main Category)`
                }
                value={
                  categoriesData?.find((c) => c.id === formData.categoryId) || null
                }
                onChange={(event, newValue) => {
                  setFormData((prev) => ({
                    ...prev,
                    categoryId: newValue ? newValue.id : '',
                  }));
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'input') {
                    setCategorySearch(newInputValue);
                  } else if (reason === 'clear') {
                    setCategorySearch('');
                  }
                }}
                filterOptions={(x) => x}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Category" 
                    helperText="Search and select a category"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Brand"
                name="brand"
                value={formData.brand}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label="Base Unit"
                name="baseUnit"
                value={formData.baseUnit}
                onChange={handleChange}
                helperText="Recipe / single item measurement unit"
              >
                <MenuItem value="pcs">Pieces (pcs)</MenuItem>
                <MenuItem value="box">Box</MenuItem>
                <MenuItem value="pack">Pack</MenuItem>
                <MenuItem value="kg">Kilogram (kg)</MenuItem>
                <MenuItem value="g">Gram (g)</MenuItem>
                <MenuItem value="litre">Litre (L)</MenuItem>
                <MenuItem value="ml">Millilitre (mL)</MenuItem>
                <MenuItem value="galn">Gallon (galn)</MenuItem>
                <MenuItem value="nos">Numbers (nos)</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label="Purchase Unit"
                name="purchaseUnit"
                value={formData.purchaseUnit}
                onChange={handleChange}
                helperText="Unit used when buying from suppliers"
              >
                <MenuItem value="pcs">Pieces (pcs)</MenuItem>
                <MenuItem value="box">Box</MenuItem>
                <MenuItem value="pack">Pack</MenuItem>
                <MenuItem value="kg">Kilogram (kg)</MenuItem>
                <MenuItem value="g">Gram (g)</MenuItem>
                <MenuItem value="litre">Litre (L)</MenuItem>
                <MenuItem value="ml">Millilitre (mL)</MenuItem>
                <MenuItem value="galn">Gallon (galn)</MenuItem>
                <MenuItem value="nos">Numbers (nos)</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="number"
                label="Conversion Factor"
                name="conversionFactor"
                value={formData.conversionFactor}
                onChange={handleChange}
                helperText={`How many ${formData.baseUnit} are inside one ${formData.purchaseUnit}`}
                inputProps={{ min: 0.01, step: 0.01 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={`Cost Price (${currency})`}
                name="costPrice"
                type="text"
                inputMode="decimal"
                placeholder={`Cost Price (${currency})`}
                value={formData.costPrice === 0 || formData.costPrice === '0' ? '' : formData.costPrice}
                onChange={handleChange}
                inputProps={{ inputMode: 'decimal', pattern: '[0-9]*\\.?[0-9]*' }}
              />
            </Grid>
            {(!isEdit || !product?.openingStock || Number(product?.openingStock) === 0) ? (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Opening Stock"
                  name="openingStock"
                  type="text"
                  inputMode="decimal"
                  value={formData.openingStock === 0 || formData.openingStock === '0' ? '' : formData.openingStock}
                  onChange={handleChange}
                  placeholder="Opening Stock"
                  helperText={isEdit ? "Sets initial stock at Main Warehouse (can only be set once)." : "Optional. Sets initial stock at Main Warehouse when creating the product."}
                  inputProps={{ inputMode: 'decimal', pattern: '[0-9]*\\.?[0-9]*' }}
                />
              </Grid>
            ) : (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Opening Stock (reference)"
                  name="openingStock"
                  value={formData.openingStock}
                  InputProps={{ readOnly: true }}
                  helperText="Opening stock is set at creation; edit stock via Inventory."
                />
              </Grid>
            )}
            <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
                    color="primary"
                  />
                }
                label="Active Status"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button type="submit" variant="contained">
                  {isEdit ? 'Update' : 'Create'}
                </Button>
                <Button variant="outlined" onClick={() => navigate('/products')}>
                  Cancel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Dialog
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Scan Barcode</DialogTitle>
        <DialogContent>
          {scanError ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {scanError}
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              Point the camera at the barcode.
            </Alert>
          )}
          {scanOpen && (
            <BarcodeScannerComponent
              width="100%"
              height={360}
              delay={200}
              constraints={{ facingMode: 'environment' }}
              onUpdate={(err, result) => {
                if (err) {
                  if (err?.name === 'NotAllowedError') {
                    setScanError('Camera permission denied. Please allow camera access and try again.');
                  } else if (err?.name !== 'NotFoundException') {
                    setScanError('Unable to read barcode. Try better lighting or move closer.');
                  }
                  return;
                }
                const text = result?.text ? String(result.text) : '';
                if (text) {
                  setFormData((prev) => ({ ...prev, barcode: text }));
                  setScanOpen(false);
                }
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => {
              setScanOpen(false);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ProductForm;
